import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { readFileAsText, parseCSVExpenseData, ParsedExpenseData } from '../utils/csvParser';

interface BulkUploadProps {
  onUploadComplete?: (result: any) => void;
  onClose?: () => void;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  result: any | null;
  error: string | null;
}

const BulkUpload: React.FC<BulkUploadProps> = ({ onUploadComplete, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ParsedExpenseData[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    result: null,
    error: null
  });

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  // Handle file selection via input
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Process selected file
  const handleFile = async (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadState(prev => ({ ...prev, error: 'Please select a CSV file' }));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadState(prev => ({ ...prev, error: 'File size must be less than 10MB' }));
      return;
    }

    setSelectedFile(file);
    setUploadState({ isUploading: false, progress: 0, result: null, error: null });

    try {
      // Read and parse CSV for preview
      const content = await readFileAsText(file);
      const parseResult = parseCSVExpenseData(content);

      if (parseResult.errors.length > 0) {
        setUploadState(prev => ({
          ...prev,
          error: `CSV parsing errors: ${parseResult.errors.slice(0, 3).join(', ')}${parseResult.errors.length > 3 ? '...' : ''}`
        }));
        return;
      }

      setPreviewData(parseResult.data);
      setShowPreview(true);

    } catch (error) {
      setUploadState(prev => ({
        ...prev,
        error: `Failed to read file: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadState(prev => ({ ...prev, isUploading: true, progress: 0, error: null }));

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadState(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90)
        }));
      }, 200);

      const result = await apiClient.bulkUploadExpenses(selectedFile);

      clearInterval(progressInterval);
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        progress: 100,
        result
      }));

      if (onUploadComplete) {
        onUploadComplete(result);
      }

    } catch (error: any) {
      console.error('Bulk upload error:', error);
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: error.message || 'Upload failed. Please check if the backend server is running on http://localhost:8000'
      }));
    }
  };

  // Reset component state
  const handleReset = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setShowPreview(false);
    setUploadState({ isUploading: false, progress: 0, result: null, error: null });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Bulk Upload Expenses</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!selectedFile ? (
            /* File Upload Area */
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-400 bg-blue-400/10'
                  : 'border-white/20 hover:border-white/30'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                id="csv-file-input"
              />

              <div className="flex flex-col items-center gap-4">
                <div className={`p-4 rounded-full ${dragActive ? 'bg-blue-400/20' : 'bg-white/5'}`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-blue-400' : 'text-slate-400'}`} />
                </div>

                <div>
                  <p className="text-white font-medium mb-2">
                    {dragActive ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
                  </p>
                  <p className="text-slate-400 text-sm mb-4">
                    or click to browse • Max 10MB • CSV format only
                  </p>

                  {/* Visible file input as fallback */}
                  <label htmlFor="csv-file-input" className="cursor-pointer">
                    <span className="inline-flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md transition-colors">
                      Choose CSV File
                    </span>
                  </label>
                </div>

                <div className="text-xs text-slate-500 mt-4">
                  <p className="font-medium mb-2">Expected CSV format:</p>
                  <div className="bg-gray-800 p-3 rounded text-left font-mono text-xs">
                    <div>amount,date,description,category</div>
                    <div>100.50,2024-01-15,Lunch,Food</div>
                    <div>25.00,2024-01-16,Bus fare,Transport</div>
                  </div>
                </div>
              </div>
            </div>
          ) : showPreview ? (
            /* Preview and Upload */
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                <FileText className="h-5 w-5 text-blue-400" />
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedFile.name}</p>
                  <p className="text-slate-400 text-sm">
                    {previewData.length} expenses ready to upload
                  </p>
                </div>
                <button
                  onClick={handleReset}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Preview Table */}
              <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h4 className="text-white font-medium">Preview (first 5 rows)</h4>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Category</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {previewData.slice(0, 5).map((expense, index) => (
                        <tr key={index} className="hover:bg-white/5">
                          <td className="px-4 py-2 text-sm text-white">₹{expense.amount}</td>
                          <td className="px-4 py-2 text-sm text-slate-300">{expense.date}</td>
                          <td className="px-4 py-2 text-sm text-slate-300">{expense.description || '-'}</td>
                          <td className="px-4 py-2 text-sm text-slate-300">{expense.category_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.length > 5 && (
                  <div className="p-3 bg-white/5 border-t border-white/10 text-center">
                    <p className="text-slate-400 text-sm">
                      ... and {previewData.length - 5} more rows
                    </p>
                  </div>
                )}
              </div>

              {/* Upload Progress/Error */}
              {uploadState.isUploading && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
                    <p className="text-white font-medium">Uploading expenses...</p>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadState.progress}%` }}
                    />
                  </div>
                  <p className="text-slate-400 text-sm mt-2">{uploadState.progress}% complete</p>
                </div>
              )}

              {uploadState.error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <p className="text-red-400 font-medium">Upload Error</p>
                  </div>
                  <p className="text-red-300 text-sm mt-1">{uploadState.error}</p>
                </div>
              )}

              {uploadState.result && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <p className="text-green-400 font-medium">Upload Complete!</p>
                  </div>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>✅ Created {uploadState.result.created} expenses</p>
                    {uploadState.result.categories_created.length > 0 && (
                      <p>📁 Created {uploadState.result.categories_created.length} new categories</p>
                    )}
                    {uploadState.result.errors.length > 0 && (
                      <p>⚠️ {uploadState.result.errors.length} errors occurred</p>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleUpload}
                  disabled={uploadState.isUploading || uploadState.result}
                  className="flex-1 px-4 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md transition-colors font-medium"
                >
                  {uploadState.isUploading ? 'Uploading...' : 'Upload Expenses'}
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Upload Different File
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;